import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useCurrentUserEntity } from '@/hooks/useCurrentUserEntity';
import { crmObjectListSentinelQuery } from '@/lib/crm/store';
import type { AssociationFilter, FilterGroup } from '@/types/crm';

export interface UseMyIntegrationEntityIdsResult {
  integrationEntityIds: string[];
  isLoading: boolean;
  isLoaded: boolean;
  error: Error | null;
}

export function useMyIntegrationEntityIds(
  orgId: number | undefined
): UseMyIntegrationEntityIdsResult {
  const { entityId, isLoading: isEntityLoading } = useCurrentUserEntity();

  const filterGroups: FilterGroup[] = useMemo(() => {
    if (!entityId) {
      return [];
    }
    return [
      {
        filters: [
          {
            association: 'ahoy_integration_to_owner',
            operator: 'exact',
            value: entityId,
          } as AssociationFilter,
        ],
      },
    ];
  }, [entityId]);

  const queryConfig = crmObjectListSentinelQuery(
    orgId ?? 0,
    'ahoy_external_integration',
    { filter_groups: filterGroups }
  );

  const { data, isLoading, isSuccess, error } = useQuery({
    ...queryConfig,
    enabled: !!orgId && !!entityId,
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
  });

  return useMemo(
    () => ({
      // Sort to keep downstream React Query cache keys stable across
      // equivalent ID sets — the backend does not guarantee a deterministic
      // result order, so an unsorted list would silently fragment caches
      // (useCalendarData, buildTodayMeetingsFilters, CalendarSearchBar all
      // embed these IDs in filter_groups, which participate in the query key).
      integrationEntityIds: (data?.ids ?? []).slice().sort(),
      isLoading: isLoading || isEntityLoading,
      // "Loaded" means we have a definitive answer: the query succeeded,
      // the query errored, OR the entity check completed and the user has
      // no CRM entity (so integrations are definitively empty). Without
      // the third condition, a disabled query (entityId null) leaves
      // isLoaded=false and isLoading=false simultaneously — a limbo state
      // that causes useCalendarEvents to report isLoading=false with empty
      // integrationEntityIds, flashing "Connect your calendar" before
      // OrgContext resolves the user's entity.
      isLoaded: isSuccess || error !== null || (!isEntityLoading && !entityId),
      error: error ?? null,
    }),
    [data, isLoading, isEntityLoading, isSuccess, error, entityId]
  );
}
